import { Fragment, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import Attachments from "../components/Attachments";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
    ClipboardList, Truck, Plus, X, Printer, Check, Send, Undo2,
    AlertTriangle, Trash2, PackageCheck,
} from "lucide-react";

const num = (v, d = 0) =>
    Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const kg = (v) => `${num(v, 2)} kg`;
const dt = (v) => {
    if (!v) return "—";
    try { return new Date(v).toLocaleString("en-PH", { timeZone: "Asia/Manila" }); } catch { return v; }
};

const COMPANIES = [
    { value: "DK Homestyle", label: "DK Homestyle Corp", sub: "Non-Organic" },
    { value: "Dwellstone", label: "Dwellstone Inc", sub: "Organic" },
];

const CLASSES = [
    { value: "ADV", label: "ADV — Advance", hint: "ATP received before 4pm today, pick up the following day" },
    { value: "R", label: "R — Regular", hint: "ATP received today and picked up today (or after the 4pm cut-off)" },
    { value: "EL", label: "EL — Express Lane", hint: "3rd-party logistics (Lalamove, Grab, etc.), regardless of ATP timing" },
];

// ------------------------------------------------------------------ list page
export default function PickAndPack() {
    const { user, hasPermission } = useAuth();
    const canWrite = user?.role === "admin" || hasPermission?.("pickpack.write");
    const [rows, setRows] = useState([]);
    const [company, setCompany] = useState("");
    const [status, setStatus] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [detailId, setDetailId] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams();
            if (company) p.set("company", company);
            if (status) p.set("status", status);
            const { data } = await api.get(`/pickpack${p.toString() ? "?" + p : ""}`);
            setRows(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [company, status]);

    return (
        <div className="p-6 space-y-4" data-testid="pickpack-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="sc-section-title">Logistics</div>
                    <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
                        <PackageCheck className="w-6 h-6" /> Pick &amp; Pack
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Build a pick list from approved orders, weigh it against the truck, route it through approval, and release against a signed pull-out slip.
                    </p>
                </div>
                {canWrite && (
                    <button onClick={() => setCreateOpen(true)} data-testid="pp-new"
                        className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-2">
                        <Plus className="w-4 h-4" /> New Pick List
                    </button>
                )}
            </div>

            <div className="flex flex-wrap gap-2">
                <select value={company} onChange={(e) => setCompany(e.target.value)} className="h-9 px-2 rounded-md border border-border text-sm bg-background">
                    <option value="">All companies</option>
                    {COMPANIES.map((c) => <option key={c.value} value={c.value}>{c.label} ({c.sub})</option>)}
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 px-2 rounded-md border border-border text-sm bg-background">
                    <option value="">All statuses</option>
                    {["draft", "pending_approval", "approved", "released", "rejected"].map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
                </select>
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1000px]">
                        <thead>
                            <tr>
                                <th>Pick List</th>
                                <th>Company</th>
                                <th>Truck</th>
                                <th className="text-right">Weight / Capacity</th>
                                <th>Status</th>
                                <th>Created by</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="cursor-pointer hover:bg-secondary/40" onClick={() => setDetailId(r.id)} data-testid={`pp-row-${r.doc_no}`}>
                                    <td className="font-semibold">{r.doc_no}
                                        <div className="text-[10px] text-muted-foreground">{r.ref_no}</div></td>
                                    <td>{r.company}<div className="text-[10px] text-muted-foreground">{r.order_source === "non_organic" ? "Non-Organic" : "Organic"}</div></td>
                                    <td className="whitespace-nowrap"><Truck className="w-3.5 h-3.5 inline mr-1" />{r.truck_label || r.truck_type}</td>
                                    <td className="sc-num text-right">
                                        <span className={r.over_capacity ? "text-red-600 font-semibold" : ""}>{kg(r.total_weight_kg)}</span>
                                        <span className="text-muted-foreground"> / {kg(r.truck_capacity_kg)}</span>
                                        {r.over_capacity && <div className="text-[10px] text-red-600 font-semibold">OVER CAPACITY</div>}
                                    </td>
                                    <td><StatusBadge status={r.status} /></td>
                                    <td className="text-sm">{r.created_by_name || r.created_by || "—"}</td>
                                    <td className="text-sm whitespace-nowrap">{r.pp_date}</td>
                                </tr>
                            ))}
                            {!rows.length && (
                                <tr><td colSpan={7} className="text-center text-sm text-muted-foreground py-10">
                                    {loading ? "Loading…" : "No pick lists yet."}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {createOpen && <CreateModal onClose={() => setCreateOpen(false)} onCreated={(id) => { setCreateOpen(false); load(); setDetailId(id); }} />}
            {detailId && <DetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={load} />}
        </div>
    );
}

// ------------------------------------------------------------------ create
function CreateModal({ onClose, onCreated }) {
    const [company, setCompany] = useState("DK Homestyle");
    const [trucks, setTrucks] = useState([]);
    const [truckType, setTruckType] = useState("");
    const [seqClass, setSeqClass] = useState("R");
    const [remarks, setRemarks] = useState("");
    const [orders, setOrders] = useState([]);
    const [q, setQ] = useState("");
    const [picked, setPicked] = useState([]);          // {key, sales_order_id, po_no, item_code, item_name, uom, weight_per_pc, max, quantity, remarks}
    const [busy, setBusy] = useState(false);
    const [expand, setExpand] = useState(null);

    useEffect(() => { api.get("/pickpack/truck-types").then(({ data }) => { setTrucks(data || []); if (data?.[0]) setTruckType(data[0].code); }).catch(() => { }); }, []);
    useEffect(() => {
        const p = new URLSearchParams({ company }); if (q) p.set("q", q);
        api.get(`/pickpack/eligible-orders?${p}`).then(({ data }) => setOrders(data || [])).catch((e) => toast.error(formatApiError(e)));
        setPicked([]);  // company changed -> clear
    }, [company, q]);

    const truck = trucks.find((t) => t.code === truckType);
    const total = useMemo(() => picked.reduce((a, l) => a + Number(l.weight_per_pc || 0) * Number(l.quantity || 0), 0), [picked]);
    const cap = Number(truck?.capacity_kg || 0);
    const over = cap > 0 && total > cap + 0.0005;

    const addLine = (o, ln) => {
        const key = `${o.sales_order_id}:${ln.item_code}`;
        if (picked.some((p) => p.key === key)) { toast.message("Already added"); return; }
        setPicked((prev) => [...prev, {
            key, sales_order_id: o.sales_order_id, po_no: o.po_no, item_code: ln.item_code,
            item_name: ln.item_name, uom: ln.uom, weight_per_pc: ln.weight_per_pc, max: ln.quantity,
            quantity: ln.quantity, remarks: "",
        }]);
    };
    const setQty = (key, v) => setPicked((prev) => prev.map((p) => p.key === key ? { ...p, quantity: v } : p));
    const setRem = (key, v) => setPicked((prev) => prev.map((p) => p.key === key ? { ...p, remarks: v } : p));
    const drop = (key) => setPicked((prev) => prev.filter((p) => p.key !== key));

    const save = async () => {
        if (!picked.length) return toast.error("Add at least one line");
        if (!truckType) return toast.error("Choose a truck");
        setBusy(true);
        try {
            const { data } = await api.post("/pickpack", {
                company, truck_type: truckType, seq_class: seqClass, remarks,
                lines: picked.map((p) => ({
                    sales_order_id: p.sales_order_id, po_no: p.po_no, item_code: p.item_code,
                    item_name: p.item_name, quantity: Number(p.quantity || 0), remarks: p.remarks,
                })),
            });
            toast.success(`Pick list ${data.doc_no} created`);
            onCreated(data.id);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div className="sc-card w-full max-w-4xl max-h-[92vh] overflow-y-auto p-5 space-y-4 bg-background" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <div className="font-semibold text-lg flex items-center gap-2"><ClipboardList className="w-5 h-5" /> New Pick List</div>
                    <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded hover:bg-secondary"><X className="w-4 h-4" /></button>
                </div>

                <div className="grid sm:grid-cols-3 gap-3">
                    <label className="text-sm space-y-1">
                        <span className="text-muted-foreground">Company</span>
                        <select value={company} onChange={(e) => setCompany(e.target.value)} className="w-full h-9 px-2 rounded-md border border-border bg-background">
                            {COMPANIES.map((c) => <option key={c.value} value={c.value}>{c.label} ({c.sub})</option>)}
                        </select>
                    </label>
                    <label className="text-sm space-y-1">
                        <span className="text-muted-foreground">Truck</span>
                        <select value={truckType} onChange={(e) => setTruckType(e.target.value)} className="w-full h-9 px-2 rounded-md border border-border bg-background">
                            {trucks.map((t) => <option key={t.code} value={t.code}>{t.label} — {num(t.capacity_kg)} kg{t.placeholder ? " (default)" : ""}</option>)}
                        </select>
                    </label>
                    <label className="text-sm space-y-1">
                        <span className="text-muted-foreground">Class</span>
                        <select value={seqClass} onChange={(e) => setSeqClass(e.target.value)} className="w-full h-9 px-2 rounded-md border border-border bg-background">
                            {CLASSES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                    </label>
                </div>
                <p className="text-xs text-muted-foreground">{CLASSES.find((c) => c.value === seqClass)?.hint}</p>

                {/* weight meter */}
                <div className={`rounded-md border p-3 ${over ? "border-red-300 bg-red-50" : "border-border bg-secondary/30"}`}>
                    <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">Total weight</span>
                        <span className={over ? "text-red-600 font-semibold" : "font-semibold"}>{kg(total)} / {kg(cap)}</span>
                    </div>
                    <div className="h-2 mt-2 rounded bg-border overflow-hidden">
                        <div className={`h-full ${over ? "bg-red-500" : "bg-primary"}`} style={{ width: `${cap ? Math.min(100, (total / cap) * 100) : 0}%` }} />
                    </div>
                    {over && <div className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Over the truck capacity by {kg(total - cap)}.</div>}
                </div>

                {/* picked lines */}
                {picked.length > 0 && (
                    <div className="sc-card overflow-hidden">
                        <table className="w-full sc-table">
                            <thead><tr><th>PO</th><th>Item</th><th className="text-right">Qty</th><th className="text-right">Wt/pc</th><th className="text-right">Line wt</th><th>Remarks</th><th></th></tr></thead>
                            <tbody>
                                {picked.map((p) => (
                                    <tr key={p.key}>
                                        <td className="text-sm">{p.po_no}</td>
                                        <td className="text-sm">{p.item_code}<div className="text-[11px] text-muted-foreground">{p.item_name}</div></td>
                                        <td className="text-right"><input type="number" min="0" max={p.max} value={p.quantity} onChange={(e) => setQty(p.key, e.target.value)} className="w-20 h-8 px-1 text-right rounded border border-border bg-background" /></td>
                                        <td className="sc-num text-right">{num(p.weight_per_pc, 2)}</td>
                                        <td className="sc-num text-right">{kg(Number(p.weight_per_pc || 0) * Number(p.quantity || 0))}</td>
                                        <td><input value={p.remarks} onChange={(e) => setRem(p.key, e.target.value)} placeholder="line remarks" className="w-full h-8 px-2 rounded border border-border bg-background text-sm" /></td>
                                        <td><button onClick={() => drop(p.key)} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 className="w-4 h-4" /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* order picker */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">Add items from an order</div>
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search PO / customer" className="h-9 px-2 rounded-md border border-border bg-background text-sm w-56" />
                    </div>
                    <div className="sc-card max-h-64 overflow-y-auto divide-y divide-border">
                        {orders.map((o) => (
                            <div key={o.sales_order_id} className="p-2">
                                <button onClick={() => setExpand(expand === o.sales_order_id ? null : o.sales_order_id)} className="w-full text-left text-sm flex items-center justify-between">
                                    <span><b>{o.doc_no}</b> · {o.customer_name || o.customer_code} <span className="text-muted-foreground">({o.status?.replaceAll("_", " ")})</span></span>
                                    <span className="text-xs text-muted-foreground">{o.lines.length} item{o.lines.length === 1 ? "" : "s"}</span>
                                </button>
                                {expand === o.sales_order_id && (
                                    <div className="mt-2 space-y-1">
                                        {o.lines.map((ln) => (
                                            <div key={ln.item_code} className="flex items-center justify-between text-sm pl-2">
                                                <span>{ln.item_code} <span className="text-muted-foreground">{ln.item_name}</span> · {num(ln.quantity)} {ln.uom || "pcs"} · {num(ln.weight_per_pc, 2)} kg/pc</span>
                                                <button onClick={() => addLine(o, ln)} className="h-7 px-2 rounded border border-border text-xs inline-flex items-center gap-1 hover:bg-secondary"><Plus className="w-3 h-3" /> Add</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        {!orders.length && <div className="p-6 text-center text-sm text-muted-foreground">No eligible orders for this company.</div>}
                    </div>
                </div>

                <label className="text-sm space-y-1 block">
                    <span className="text-muted-foreground">Remarks (depend on the pick-list line-up)</span>
                    <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className="w-full rounded-md border border-border bg-background p-2 text-sm" />
                </label>

                <div className="flex items-center justify-end gap-2 pt-1">
                    <button onClick={onClose} className="h-9 px-3 rounded-md border border-border text-sm">Cancel</button>
                    <button onClick={save} disabled={busy || !picked.length} className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">{busy ? "Saving…" : "Create draft"}</button>
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------ detail
function DetailModal({ id, onClose, onChanged }) {
    const { user, hasPermission } = useAuth();
    const canWrite = user?.role === "admin" || hasPermission?.("pickpack.write");
    const canApprove = user?.role === "admin" || hasPermission?.("pickpack.approve");
    const [pp, setPp] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => { try { const { data } = await api.get(`/pickpack/${id}`); setPp(data); } catch (e) { toast.error(formatApiError(e)); onClose(); } };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const act = async (verb, body) => {
        setBusy(true);
        try { await api.post(`/pickpack/${id}/${verb}`, body || {}); toast.success(verb.replace(/^\w/, (c) => c.toUpperCase()) + "d"); await load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    const del = async () => {
        if (!window.confirm("Delete this draft pick list?")) return;
        try { await api.delete(`/pickpack/${id}`); toast.success("Deleted"); onChanged?.(); onClose(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    if (!pp) return null;
    const over = pp.over_capacity;
    const st = pp.status;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div className="sc-card w-full max-w-3xl max-h-[92vh] overflow-y-auto p-5 space-y-4 bg-background" onClick={(e) => e.stopPropagation()} id="pp-print-area">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-xl font-semibold flex items-center gap-2">{pp.doc_no} <StatusBadge status={st} /></div>
                        <div className="text-xs text-muted-foreground">{pp.ref_no} · {pp.company} ({pp.order_source === "non_organic" ? "Non-Organic" : "Organic"}) · {pp.pp_date}</div>
                    </div>
                    <div className="flex items-center gap-1 no-print">
                        <button onClick={() => window.print()} className="h-8 px-2 rounded border border-border text-sm inline-flex items-center gap-1"><Printer className="w-4 h-4" /> Print</button>
                        <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded hover:bg-secondary"><X className="w-4 h-4" /></button>
                    </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 text-sm">
                    <Field label="Truck" value={`${pp.truck_label || pp.truck_type}`} />
                    <Field label="Capacity" value={kg(pp.truck_capacity_kg)} />
                    <Field label="Total weight" value={<span className={over ? "text-red-600 font-semibold" : ""}>{kg(pp.total_weight_kg)}</span>} />
                </div>
                {over && <div className="rounded-md border border-red-300 bg-red-50 text-red-700 text-sm p-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> This pick list is over the truck capacity by {kg(pp.total_weight_kg - pp.truck_capacity_kg)}.</div>}

                {pp.cutoff && (
                    <div className="text-xs text-muted-foreground">
                        Manila now {pp.cutoff.now_manila} · PL cut-off {pp.cutoff.pl_cutoff}{pp.cutoff.pl_cutoff_passed ? " (passed)" : ""} · truck cut-off {pp.cutoff.truck_cutoff}{pp.cutoff.truck_cutoff_passed ? " (passed)" : ""}{pp.cutoff.is_ulymar ? " · ULYMAR schedule" : ""}
                    </div>
                )}

                <div className="sc-card overflow-hidden">
                    <table className="w-full sc-table">
                        <thead><tr><th>PO</th><th>Item</th><th className="text-right">Qty</th><th className="text-right">Wt/pc</th><th className="text-right">Line wt</th><th>Remarks</th></tr></thead>
                        <tbody>
                            {(pp.lines || []).map((l) => (
                                <tr key={l.id}>
                                    <td className="text-sm">{l.po_no}<div className="text-[11px] text-muted-foreground">{l.customer_name}</div></td>
                                    <td className="text-sm">{l.item_code}<div className="text-[11px] text-muted-foreground">{l.item_name}</div></td>
                                    <td className="sc-num text-right">{num(l.quantity)} {l.uom || ""}</td>
                                    <td className="sc-num text-right">{num(l.weight_per_pc, 2)}</td>
                                    <td className="sc-num text-right">{kg(l.line_weight_kg)}</td>
                                    <td className="text-sm">{l.remarks || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {pp.remarks && <div className="text-sm"><span className="text-muted-foreground">Remarks: </span>{pp.remarks}</div>}
                {st === "rejected" && pp.reject_reason && <div className="text-sm text-red-600">Rejected: {pp.reject_reason}</div>}

                <div className="no-print">
                    <Attachments collection="pickpack" docId={pp.id} editable={canWrite && st !== "rejected"}
                        kinds={[["pull_out_slip", "Signed Pull-Out Slip"], ["pick_list", "Pick List"], ["other", "Other"]]}
                        defaultKind="pull_out_slip" />
                </div>

                {/* actions */}
                <div className="flex flex-wrap items-center justify-end gap-2 pt-1 no-print">
                    {st === "draft" && canWrite && (
                        <>
                            <button onClick={del} className="h-9 px-3 rounded-md border border-red-300 text-red-600 text-sm inline-flex items-center gap-1"><Trash2 className="w-4 h-4" /> Delete</button>
                            <button onClick={() => act("submit")} disabled={busy} className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium inline-flex items-center gap-1 disabled:opacity-50"><Send className="w-4 h-4" /> Submit for approval</button>
                        </>
                    )}
                    {st === "pending_approval" && canApprove && (
                        <>
                            <button onClick={() => { const r = window.prompt("Reason for rejection?"); if (r !== null) act("reject", { reason: r }); }} disabled={busy} className="h-9 px-3 rounded-md border border-red-300 text-red-600 text-sm inline-flex items-center gap-1"><Undo2 className="w-4 h-4" /> Reject</button>
                            <button onClick={() => act("approve")} disabled={busy} className="h-9 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium inline-flex items-center gap-1 disabled:opacity-50"><Check className="w-4 h-4" /> Approve</button>
                        </>
                    )}
                    {st === "approved" && canApprove && (
                        <button onClick={() => act("release")} disabled={busy} className="h-9 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium inline-flex items-center gap-1 disabled:opacity-50"><PackageCheck className="w-4 h-4" /> Release (pull-out)</button>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, value }) {
    return <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div><div className="font-medium">{value}</div></div>;
}
